import { useEffect, useState, Fragment } from 'react';
import classNames from 'classnames';

import { notNil } from '@/lib/utils';
import { isBasePipeline, isPipelineWithParent } from '@/core/pipeline';
import { Icon } from '@/components/icon';
import { Button } from '@/components/button';

import { getCellByPipelineIdOrThrow, isCellWithPipeline, isRecordsLikeCell } from '@/core/cell';
import { extractCombineSteps } from '@/core/operation';
import { generateShortId } from '@/lib/utils/string';

import { JoinPipelineOperation, JoinPipelineStep, Pipeline } from '../../types';
import { useMetadataContext } from '../../metadata-context';
import { useExplorationContext } from '../exploration-context';
import { useExplorationCellContext } from '../exploration-cell-context';
import { CombineStep, CombineStepState } from './types';
import { CombineItemEditor } from './combine-item-editor';
import { getModelOrThrow } from '../../model/utils';
import { convertItemToStep, getDefaultJoinKey, pickJoinKeyForStep } from './utils';

import form from '@/components/form/form.module.scss';
import style from './combine.module.scss';

interface CombineEditorProps {
  operation?: JoinPipelineOperation;
  onSubmit?: () => void;
  onCancel?: () => void;
  onAddCombine: (items: CombineStep[]) => void;
  onUpdateCombine: (items: CombineStep[]) => void;
}

export const CombineEditor = (props: CombineEditorProps) => {
  const { exploration, getFieldsByCellId, getCellById } = useExplorationContext();
  const { cell } = useExplorationCellContext();
  const { models } = useMetadataContext();

  if (!isCellWithPipeline(cell)) {
    throw new Error('CombineEditor requires a cell with a pipeline');
  }

  const buildItem = (
    input:
      | { fromCellId: string; stepId?: string }
      | { pipeline: Pipeline; key: string; stepId?: string },
  ): CombineStepState | undefined => {
    if ('pipeline' in input) {
      const pipeline = input.pipeline;
      if (isPipelineWithParent(pipeline)) {
        const cell = getCellByPipelineIdOrThrow(exploration.view.cells, pipeline.parentId);
        if (isRecordsLikeCell(cell)) {
          const fields = getFieldsByCellId(cell.id);

          return {
            stepId: input.stepId ?? generateShortId(),
            kind: 'cell',
            cells: [cell],
            cellId: cell.id,
            title: cell.title ?? '',
            fields,
            joinKey: input.key,
          };
        }
      }

      if (isBasePipeline(pipeline)) {
        const model = getModelOrThrow(models, pipeline.baseModelId);
        const fields = model.properties.map(({ key, name, type }) => ({ key, name, type }));

        return {
          stepId: input.stepId ?? generateShortId(),
          kind: 'model',
          modelId: pipeline.baseModelId,
          title: model.name,
          fields,
          joinKey: input.key,
        };
      }
    }

    if ('fromCellId' in input) {
      const baseCell = getCellById(input.fromCellId);
      if (baseCell !== undefined && isRecordsLikeCell(baseCell)) {
        const fields = getFieldsByCellId(baseCell.id);

        return {
          stepId: input.stepId ?? generateShortId(),
          kind: 'cell',
          cells: [baseCell],
          cellId: baseCell.id,
          title: baseCell.title ?? '',
          fields,
          joinKey: getDefaultJoinKey(fields),
        };
      }
    }

    return undefined;
  };

  const buildItems = (operation: JoinPipelineOperation | undefined) => {
    const steps: JoinPipelineStep[] | undefined =
      operation === undefined ? undefined : extractCombineSteps(operation);

    const inputLeft =
      props.operation === undefined
        ? buildItem({ fromCellId: cell.id })
        : buildItem({
            stepId: steps?.at(0)?.stepId,
            pipeline: cell.pipeline,
            key: steps?.at(0)?.joinStrategy?.joinKeyOnBase ?? '',
          });

    const itemsInput = [inputLeft].concat(
      steps?.map((step) =>
        buildItem({
          stepId: step.stepId,
          pipeline: step.pipeline,
          key: step.joinStrategy?.joinKeyOnRelated ?? '',
        }),
      ),
    );

    return itemsInput;
  };

  const [items, setItems] = useState<(CombineStepState | undefined)[]>(buildItems(props.operation));

  useEffect(
    () => setItems(buildItems(props.operation)),
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.operation],
  );

  const handleSubmit = (items: CombineStep[]) => {
    props.onAddCombine(items);
    props.onSubmit?.();
  };

  const emitOutput = (items: Array<CombineStepState | undefined>) => {
    setItems(items);

    const steps = items.map(convertItemToStep);
    if (steps.some((i) => i === undefined) || props.operation === undefined) {
      return;
    }

    props.onUpdateCombine(steps.filter(notNil));
  };

  const addItem = (item: CombineStepState) =>
    emitOutput([...items, pickJoinKeyForStep(items.at(-1), item)]);

  const updateItemAt = (idx: number, item: CombineStepState) =>
    emitOutput(
      [...items.slice(0, idx), item, ...items.slice(idx + 1)].reduce(
        (memo, item, i) => [...memo, i > idx ? pickJoinKeyForStep(memo.at(-1), item) : item],
        [] as (CombineStepState | undefined)[],
      ),
    );

  const deleteItemAt = (i: number) =>
    emitOutput(
      items.length <= 2
        ? [...items.slice(0, i), undefined, ...items.slice(i + 1)]
        : [...items.slice(0, i), ...items.slice(i + 1)],
    );

  const canSubmit = items.every(notNil);

  return (
    <div className={form.formContainer}>
      <div className={form.formSection}>
        <div className={form.sectionTitle}>Combined Data</div>
      </div>

      {items?.map((item, i, items) => (
        <Fragment key={i}>
          {i > 0 && (
            <div className={style.separator}>
              <div className={style.line} />
              <Icon name="Plus" size={16} color="currentColor" className={style.icon} />
              <div className={style.line} />
            </div>
          )}
          <div className={form.formSection} key={i}>
            <CombineItemEditor
              item={item}
              previousItem={i > 0 ? items.at(i - 1) : undefined}
              setItem={(item) => updateItemAt(i, item)}
              onDelete={() => deleteItemAt(i)}
            />
          </div>
        </Fragment>
      ))}

      <div className={form.formButtons}>
        <CombineItemEditor addMode setItem={(item) => addItem(item)} />
      </div>

      {props.operation === undefined && (
        <div className={classNames(form.formButtons)}>
          <Button
            onClick={() => handleSubmit(items.map(convertItemToStep).filter(notNil))}
            disabled={!canSubmit}>
            Combine
          </Button>
          {props.onCancel && (
            <Button onClick={props.onCancel} variant="outlined">
              Cancel
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
